import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Loader2, Package, DollarSign, CalendarDays, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { apiFetch } from '../lib/api';

type Range = '7d' | '30d';

interface DailyPoint {
  date: string;
  qty: number;
  revenue: number;
}

interface ProductTrend {
  productId: number;
  name: string;
  total: number;
  daily: Record<string, number>;
}

interface TrendData {
  range: Range;
  startDate: string;
  endDate: string;
  daily: DailyPoint[];
  perProduct: ProductTrend[];
  totalQty: number;
  totalRevenue: number;
}

// 範圍 → i18n key（近 7 日/近 30 日）
const RANGE_LABELS: Record<Range, string> = {
  '7d': 'partner:supplier.trends.range7d',
  '30d': 'partner:supplier.trends.range30d',
};

const CHART_MAX_HEIGHT = 160;

const SupplierSalesTrends: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem('admin_token');

  const [range, setRange] = useState<Range>('7d');
  const [data, setData] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  }, [navigate]);

  const loadTrends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/supplier/sales-trends?range=${range}`);
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('partner:supplier.trends.loadFailed'));
      setData(json as TrendData);
    } catch (e: any) {
      setError(e.message || t('partner:supplier.trends.loadFailed'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, handleUnauthorized]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadTrends();
  }, [token, navigate, loadTrends]);

  const daily = data?.daily ?? [];
  const maxQty = daily.reduce((max, d) => Math.max(max, d.qty), 0);
  const dayCount = daily.length || 1;
  const avgQty = (data?.totalQty ?? 0) / dayCount;
  const maxProductTotal = (data?.perProduct ?? []).reduce((max, p) => Math.max(max, p.total), 0);

  // 30 日模式每隔 5 日顯示一個日期標籤
  const showLabel = (index: number) => (range === '30d' ? index % 5 === 0 : true);

  return (
    <AdminLayout currentPage="supplier-sales-trends" onLogout={handleUnauthorized}>
      <div className="p-4 md:p-8">
        {/* 頁首 */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-orange-600" />
            {t('partner:supplier.trends.title')}
          </h1>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
              {(Object.keys(RANGE_LABELS) as Range[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    range === r
                      ? 'bg-orange-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(RANGE_LABELS[r])}
                </button>
              ))}
            </div>
            <LanguageSwitcher size="sm" />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-600 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            {t('partner:supplier.loading')}
          </div>
        ) : (
          <>
            {/* 統計卡片 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Package className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{t('partner:supplier.trends.periodPortions')}</div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-white">{data?.totalQty ?? 0}</div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{t('partner:supplier.trends.periodRevenue')}</div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-white">
                      HK${(data?.totalRevenue ?? 0).toLocaleString('zh-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <CalendarDays className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{t('partner:supplier.trends.dailyAvg')}</div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-white">{avgQty.toFixed(1)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CSS 柱狀圖 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {t('partner:supplier.trends.dailyChart', { start: data?.startDate, end: data?.endDate })}
              </h2>
              {daily.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-1 min-w-max" style={{ height: `${CHART_MAX_HEIGHT + 8}px` }}>
                    {daily.map((d) => {
                      const barHeight = d.qty > 0 && maxQty > 0
                        ? Math.max((d.qty / maxQty) * CHART_MAX_HEIGHT, 2)
                        : 2;
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center justify-end min-w-[16px]">
                          <div
                            className={`w-full rounded-t ${d.qty > 0 ? 'bg-orange-500' : 'bg-gray-200 dark:bg-gray-700'}`}
                            style={{ height: `${barHeight}px` }}
                            title={t('partner:supplier.trends.tooltip', { date: d.date, count: d.qty, revenue: d.revenue.toFixed(2) })}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {/* X 軸日期標籤 */}
                  <div className="flex gap-1 min-w-max mt-2">
                    {daily.map((d, i) => (
                      <div
                        key={d.date}
                        className="flex-1 min-w-[16px] text-center text-xs text-gray-600 dark:text-gray-400"
                      >
                        {showLabel(i) ? d.date.slice(5) : ''}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center text-gray-600 dark:text-gray-400">{t('partner:supplier.trends.emptyChart')}</div>
              )}
            </div>

            {/* 產品排行 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{t('partner:supplier.trends.ranking')}</h2>
              </div>
              {data && data.perProduct.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 dark:text-white">{t('partner:supplier.trends.colProduct')}</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 dark:text-white">{t('partner:supplier.trends.colPortions')}</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 dark:text-white w-1/3">{t('partner:supplier.trends.share')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {data.perProduct.map((p) => {
                      const pct = maxProductTotal > 0 ? (p.total / maxProductTotal) * 100 : 0;
                      return (
                        <tr key={p.productId}>
                          <td className="px-6 py-4 text-gray-800 dark:text-white">{p.name}</td>
                          <td className="px-6 py-4 text-right font-bold text-orange-600">{p.total}</td>
                          <td className="px-6 py-4">
                            <div className="w-full h-3 bg-orange-200 rounded-full overflow-hidden">
                              <div className="h-full bg-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="px-6 py-16 text-center text-gray-600 dark:text-gray-400">{t('partner:supplier.trends.emptyRanking')}</div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default SupplierSalesTrends;
